import type { HillGrind, HillRamp } from "./hill-course";
import type { Level, Rail } from "./level";
import type { Prim } from "./prims";
import { KICKER_PIECE } from "./prims/kicker";
import type { Compiled } from "./prims/types";

// Bomb the Hill's ramps in the park (#194, #195): the road's ramps are
// kickers in the level's primitives, after the park's own, so the sim rides
// them like any kicker. The road moves (`HillRoad.advance`) and a new run
// lays a new one out, so they're swapped in for wherever the road is now.
// Its grind rails (#237) go into the level's rails the same way.

/** Puts the hill road's ramps and rails where the road is now: the level's primitives and rails are the park's own plus them. */
export function placeRoad(level: Level): void {
  const hill = level.hill;
  if (!hill?.road) return;
  const { course } = hill.road;
  const ramps = rampPrims(course.ramps);
  // New arrays, so the lookup grids (keyed by them) are built again.
  level.prims = ramps.length ? [...hill.parkPrims, ...ramps] : hill.parkPrims;
  const rails = grindRails(course.grinds, hill.grade);
  level.rails = rails.length ? [...hill.parkRails, ...rails] : hill.parkRails;
}

/** The road's grind rails (#237) as the park's rails: down the hill from each top end, `h` over the road all the way, so falling `grade` a metre with it (`hillDrop`). */
export function grindRails(grinds: readonly HillGrind[], grade: number): Rail[] {
  return grinds.map((g) => {
    const b = g.z + g.len;
    return { a: { x: g.x, z: g.z }, b: { x: g.x, z: b }, y: g.h - grade * g.z, yb: g.h - grade * b, kind: "rail" };
  });
}

/** The road's ramps as kickers, rising toward +z (down the hill). */
export function rampPrims(ramps: readonly HillRamp[]): Prim[] {
  const out: Compiled = { prims: [], rails: [], scenery: [] };
  for (const r of ramps) KICKER_PIECE.compile({ kind: "kicker", x: r.x, z: r.z, rot: 90, len: r.len, hw: r.hw, h: r.h }, out);
  return out.prims;
}
